package gr.tech.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowRight
import com.varabyte.kobweb.silk.components.icons.fa.FaGear
import com.varabyte.kobweb.silk.components.icons.fa.FaXmark
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.ModalOverlay
import gr.tech.site.components.UsStateMap
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.data.StateInfo
import gr.tech.site.data.allStates
import gr.tech.site.styles.*
import gr.tech.site.styles.landings.LandingCtaSecondaryStyle
import gr.tech.site.styles.wideCappedWidth
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initStatesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "State",
        description = "Find utility-backed energy rebate programs and incentives for LED lighting, HVAC, and horticulture upgrades across multiple states. Maximize efficiency and lower upgrade costs for your commercial or indoor grow operation.",
        canonicalUrl = "https://greenrebates.com/states"
    ))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun StatesPage() {
    val ctx = rememberPageContext()

    // The state whose utility modal is currently open (null = closed)
    var selectedState by remember { mutableStateOf<StateInfo?>(null) }

    // Utility modal — open whenever a state without a dedicated page is selected
    ModalOverlay(
        isOpen = selectedState != null,
        onClose = { selectedState = null }
    ) {
        selectedState?.let { state ->
            StateUtilityModal(state = state, onDismiss = { selectedState = null })
        }
    }

    Main(
        attrs = Modifier
            .wideCappedWidth()
            .classNames("d-flex", "flex-column", "mt-5", "mx-auto")
            .role("main")
            .toAttrs()
    ) {

        Div(attrs = Modifier.classNames("row", "align-items-center", "gx-4", "gy-3").toAttrs()) {

            Div(attrs = Modifier.classNames("col-12", "col-md-4", "px-3").toAttrs()) {
                H1(attrs = Modifier.classNames("fs-1").toAttrs()) {
                    Text("Rebate Map")
                }

                P(attrs = Modifier.toAttrs { classes("fs-6", "mt-2", "mb-2") }) {
                    Text("Green Rebates operates nationwide, connecting growers and businesses with energy incentives in every state. Click any state on the map or use the cards below to view utility programs and get started.")
                }

                P(attrs = Modifier.toAttrs { classes("fs-6", "mb-2") }) {
                    B(attrs = Modifier.color(Theme.DarkGreen.rgb).toAttrs()) {
                        Text("Don't see your location listed?")
                    }
                    Text(" Give us a call and we'll discover rebates available in your area.")
                }

                Link(
                    path = "/contact-us",
                    text = "Contact Us →",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaSecondaryStyle.toModifier()
                        .classNames("mt-3", "d-none", "d-md-inline-block")
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact us about rebates in your area") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }

            Div(attrs = Modifier.classNames("col-12", "col-md-8").toAttrs()) {
                UsStateMap(
                    onNoPageClick = { abbr ->
                        selectedState = allStates.find { it.mapKey == abbr }
                    }
                )
                // Tier legend
                Div(
                    attrs = Modifier
                        .classNames("d-flex", "flex-wrap", "gap-3", "mt-2", "justify-content-end")
                        .fontSize(0.72.cssRem)
                        .color(rgba(0, 0, 0, 0.55))
                        .toAttrs()
                ) {
                    listOf(
                        Triple(rgb(2, 67, 66),    "Best Rebates",      true),
                        Triple(rgb(143, 191, 144), "Active Programs",   false),
                        Triple(rgb(211, 245, 211), "All States Covered", false),
                    ).forEach { (color, label, bold) ->
                        Span(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-1").toAttrs()) {
                            Span(
                                attrs = Modifier
                                    .display(DisplayStyle.InlineBlock)
                                    .width(10.px)
                                    .height(10.px)
                                    .borderRadius(50.percent)
                                    .backgroundColor(color)
                                    .flexShrink(0)
                                    .toAttrs()
                            ) {}
                            Span(
                                attrs = Modifier
                                    .fontWeight(if (bold) FontWeight.Bold else FontWeight.Normal)
                                    .toAttrs()
                            ) { Text(label) }
                        }
                    }
                }
            }
        }

        HorizontalDivider(modifier = Modifier.classNames("my-4"))

        // 50-state card grid
        Div(
            attrs = Modifier
                .classNames(
                    "row",
                    "row-cols-2",   // 2-up on mobile
                    "row-cols-sm-3", // 3-up on small
                    "row-cols-lg-4", // 4-up on desktop
                    "row-cols-xl-5", // 5-up on wide
                    "g-2",
                    "mb-5"
                )
                .toAttrs()
        ) {
            allStates.forEach { state ->
                Div(attrs = Modifier.classNames("col").toAttrs()) {
                    StateCard(
                        state = state,
                        onCardClick = {
                            if (state.statePage != null) {
                                ctx.router.navigateTo(state.statePage)
                            } else {
                                selectedState = state
                            }
                        },
                        onViewUtilitiesClick = { selectedState = state }
                    )
                }
            }
        }
    }
}

// State Card

@Composable
fun StateCard(
    state: StateInfo,
    onCardClick: () -> Unit,
    onViewUtilitiesClick: () -> Unit
) {
    Div(
        attrs = StateCardStyle.toModifier()
            .attrsModifier { onClick { onCardClick() } }
            .toAttrs()
    ) {
        // → indicator: signals this card navigates to a dedicated page
        if (state.statePage != null) {
            Span(
                attrs = StatePageArrowStyle.toModifier()
                    .classNames("state-page-arrow")
                    .toAttrs()
            ) {
                Text("more info →")
            }
        }

        // Body — abbreviation + name, centred
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .padding(top = 16.px, bottom = 12.px, leftRight = 8.px)
                .textAlign(TextAlign.Center)
                .toAttrs()
        ) {
            Div(
                attrs = StateAbbrStyle.toModifier()
                    .classNames("state-abbr")
                    .toAttrs()
            ) {
                Text(state.abbr)
            }
            Div(
                attrs = StateNameStyle.toModifier()
                    .classNames("state-name")
                    .toAttrs()
            ) {
                Text(state.name)
            }
        }

        // Footer — always opens the utility modal, never the state page
        Div(
            attrs = StateFooterStyle.toModifier()
                .classNames("state-footer")
                .attrsModifier {
                    onClick { event ->
                        event.stopPropagation()  // don't trigger card body click
                        onViewUtilitiesClick()
                    }
                }
                .toAttrs()
        ) {
            Text("↓ view utilities")
        }
    }
}

// Utility Modal Content

@Composable
fun StateUtilityModal(state: StateInfo, onDismiss: () -> Unit) {
    // Outer card shell
    Div(attrs = UtilityModalCardStyle.toModifier().toAttrs()) {

        // Dark green header
        Div(attrs = UtilityModalHeaderStyle.toModifier().toAttrs()) {

            // Left: abbr + name + subtitle
            Div {
                Div(
                    attrs = Modifier
                        .fontSize(2.6.cssRem)
                        .fontWeight(FontWeight.Bold)
                        .color(Theme.White.rgb)
                        .lineHeight(1)
                        .margin(bottom = 5.px)
                        .toAttrs()
                ) {
                    Text(state.abbr)
                }
                Div(
                    attrs = Modifier
                        .fontSize(0.95.cssRem)
                        .color(rgba(255, 255, 255, 0.75))
                        .margin(bottom = 2.px)
                        .toAttrs()
                ) {
                    Text(state.name)
                }
                if (state.statePage != null) {
                    Link(
                        path = state.statePage,
                        modifier = Modifier
                            .fontSize(0.72.cssRem)
                            .color(rgba(255, 255, 255, 0.55))
                            .letterSpacing(0.05.em)
                            .textTransform(TextTransform.Uppercase),
                        variant = UndecoratedLinkVariant,
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    ) {
                        Text("More Info →")
                    }
                } else {
                    Div(
                        attrs = Modifier
                            .fontSize(0.72.cssRem)
                            .color(rgba(255, 255, 255, 0.42))
                            .letterSpacing(0.05.em)
                            .textTransform(TextTransform.Uppercase)
                            .toAttrs()
                    ) {
                        Text("Utility Programs")
                    }
                }
            }

            // Right: close button
            Span(
                attrs = UtilityModalCloseStyle.toModifier()
                    .onClick { onDismiss() }
                    .toAttrs()
            ) {
                FaXmark(size = IconSize.LG)
            }
        }

        // Utility rows
        Div(attrs = UtilityModalBodyStyle.toModifier().toAttrs()) {
            if (state.utilities.isEmpty()) {
                // Empty state
                Div(
                    attrs = Modifier
                        .padding(topBottom = 36.px, leftRight = 24.px)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Div(
                        attrs = Modifier
                            .fontSize(2.2.cssRem)
                            .margin(bottom = 10.px)
                            .toAttrs()
                    ) {
                        FaGear(modifier = Modifier.color(Theme.MediumGreen.rgb))
                    }
                    P(
                        attrs = Modifier
                            .color(rgba(0, 0, 0, 0.5))
                            .fontSize(0.9.cssRem)
                            .margin(bottom = 4.px)
                            .toAttrs()
                    ) {
                        Text("Specific utilities for ${state.name} are being added.")
                    }
                    P(
                        attrs = Modifier
                            .color(Theme.DarkGreen.rgb)
                            .opacity(0.5)
                            .fontSize(0.8.cssRem)
                            .margin(0.px)
                            .toAttrs()
                    ) {
                        Text("Contact us and we’ll find incentives in your area.")
                    }
                }
            } else {
                state.utilities.forEach { displayName ->
                    val path = if (state.statePage != null)
                        "${state.statePage}#utilities"
                    else
                        "/contact-us?state=${state.name}&utility=$displayName"
                    Link(
                        path = path,
                        modifier = UtilityRowStyle.toModifier(),
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        openInternalLinksStrategy = if (state.statePage != null)
                            OpenLinkStrategy.IN_PLACE
                        else
                            OpenLinkStrategy.IN_NEW_TAB
                    ) {
                        // Left side: dot indicator + utility name
                        Span(attrs = Modifier.classNames("d-flex", "align-items-center", "gap-2").toAttrs()) {
                            Span(
                                attrs = Modifier
                                    .display(DisplayStyle.InlineBlock)
                                    .width(7.px)
                                    .height(7.px)
                                    .borderRadius(50.percent)
                                    .backgroundColor(Theme.MediumGreen.rgb)
                                    .flexShrink(0)
                                    .toAttrs()
                            ) {}
                            Span(
                                attrs = Modifier
                                    .fontSize(0.92.cssRem)
                                    .fontWeight(500)
                                    .toAttrs()
                            ) {
                                Text(displayName)
                            }
                        }
                        // Right side: arrow
                        FaArrowRight(
                            modifier = UtilityRowArrowStyle.toModifier().classNames("util-arrow"),
                            size = IconSize.SM
                        )
                    }
                }
            }
        }

        // Footer
        Div(attrs = UtilityModalFooterStyle.toModifier().toAttrs()) {

            // State page deep-link (only when dedicated page exists)
            if (state.statePage != null) {
                Link(
                    path = state.statePage,
                    modifier = ModalFooterStatePageStyle.toModifier(),
                    variant = UndecoratedLinkVariant,
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                ) {
                    Text("View ${state.name}")
                }
            } else {
                Span {}  // spacer so contact link stays right-aligned
            }

            Link(
                path = "/contact-us",
                modifier = ModalFooterContactStyle.toModifier(),
                variant = UndecoratedLinkVariant,
                openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE
            ) {
                Text("Can’t find yours? Contact us")
            }
        }
    }
}
